import * as React from "react";
import { Area } from "../../../models/Area";
import { UserArea } from "../../../models/UserArea";
import { UserAreaPermissions } from "../../../models/UserAreaPermissions";
import { UserDetails } from "../../../models/UserDetails";
import { AdminApiController } from "../../../models/AdminApiController.generated";
import { AreaTree } from "./areaTree";
import { UserAreaListBox } from "./userAreaListBox";
import { Button } from "react-bootstrap";
import * as _ from "lodash";
import { AreaEditModal } from "./areaEditModal";
import { AreaWithProjectName } from "../../../models/AreaWithProjectName";
import { AreaNode } from "./areaModels";
import { SetUserAreasArgs } from "../../../models/SetUserAreasArgs";
import { LicenseType } from "../../../models/LicenseType";
import { Profile } from "../../../models/Profile";
import { hasEditAreasAccess, hasViewUsersAccess, hasEditSites } from "../shared/helpers";

interface Props {
    userProfile?: Profile;
}

interface State {
    areas: AreaWithProjectName[];
    users: UserDetails[];
    userAreas: UserArea[];
    ownedUserAreas: UserArea[];
    treeData: AreaNode[];
    selectedArea: AreaNode;
    selectedUsers: { id: number, permissions: UserAreaPermissions }[];
    inheritedUsers: { id: number, permissions: UserAreaPermissions }[];
    selectedAreaUsers: { id: number, permissions: UserAreaPermissions }[];
    userSelectDirty: boolean;
    errorMessage?: string;

    // for edit sites rights
    addButtonVisible: boolean;
    editButtonVisible: boolean;
    deleteButtonVisible: boolean;
    disableUsersList: boolean;
}

export class AreasPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            areas: [],
            users: [],
            userAreas: [],
            ownedUserAreas: [],
            treeData: [],
            selectedArea: null,
            selectedUsers: [],
            inheritedUsers: [],
            selectedAreaUsers: [],
            userSelectDirty: false,
            addButtonVisible: false,
            editButtonVisible: false,
            deleteButtonVisible: false,
            disableUsersList: false
        };

        this.handleAreaSelectionChange = this.handleAreaSelectionChange.bind(this);
        this.handleUserSelectionChange = this.handleUserSelectionChange.bind(this);
        this.handleUserSelectionRemove = this.handleUserSelectionRemove.bind(this);
    }

    async componentDidMount() {
        const userAreas = await AdminApiController.getUserAreas();
        if (userAreas.result) {
            this.setState({
                userAreas: userAreas.userAreas,
                ownedUserAreas: userAreas.ownedUserAreas
            });
        }
        else {
            this.setState({ errorMessage: userAreas.message });
        }

        const areas = await AdminApiController.getAreas();
        if (areas.result) {
            const treeData = this.getTreeData(undefined, areas.areas);
            this.setState({
                areas: areas.areas,
                treeData: treeData
            })
        } else {
            this.setState({ errorMessage: areas.message });
        }

        this.getUsersList();
    }

    componentDidUpdate(prevProps) {
        if (this.props.userProfile !== prevProps.userProfile) {
            this.getUsersList();
        }
    }

    getUsersList() {
        if (hasViewUsersAccess(this.props.userProfile)) {
            AdminApiController.getUserList().then(resp => {
                if (resp.success) {
                    this.setState({
                        users: resp.users.filter(u => u.license != LicenseType.Inactive)
                    });
                }
                else {
                    this.setState({
                        errorMessage: resp.message
                    });
                }
            });
        }
    }

    updateState = async (areas: AreaWithProjectName[]) => {
        const userAreas = await AdminApiController.getUserAreas();
        this.setState({
            userAreas: userAreas.userAreas,
            ownedUserAreas: userAreas.ownedUserAreas
        });

        const treeData = this.getTreeData(undefined, areas);
        this.setState({
            areas: areas,
            treeData: treeData
        });
    };

    handleAreaSelectionChange(selectedNode: AreaNode) {
        const inherited = this.getInheritedForSelection(selectedNode);
        const areaUsers = this.getUsersForSelection(selectedNode);

        this.setState({
            selectedArea: selectedNode,
            inheritedUsers: inherited,
            selectedAreaUsers: areaUsers,
            selectedUsers: inherited.concat(areaUsers),
            userSelectDirty: false
        }, () => {
            this.updateControlsState();
        });
    }

    updateControlsState() {
        const { userProfile } = this.props;
        const { ownedUserAreas, selectedArea } = this.state;
        const inherited = this.getInheritedForSelection(selectedArea);

        // just set as true, if not edit sites rights
        var addButtonVisible = true;
        var editButtonVisible = true;
        var deleteButtonVisible = true;
        var disableUsersList = false;

        if (hasEditSites(userProfile)) {
            var editEnabledAreas = ownedUserAreas.filter((ua, i) => ua.permissions == 0);
            var isCurrentLevel = editEnabledAreas.some(ua => ua.areaId == selectedArea.id);
            var isChildLevel = inherited
                .filter((ua) => ua.permissions == 0)
                .some(i => ownedUserAreas.some(ua => ua.userId == i.id));
            var isParentLevel = !isCurrentLevel && !isChildLevel;

            addButtonVisible = (isCurrentLevel || isChildLevel) && !isParentLevel;
            editButtonVisible = !isParentLevel && !isCurrentLevel;
            deleteButtonVisible = isParentLevel && isCurrentLevel && isChildLevel;
            disableUsersList = isParentLevel || isCurrentLevel;
        }

        this.setState({
            addButtonVisible: addButtonVisible,
            editButtonVisible: editButtonVisible,
            deleteButtonVisible: deleteButtonVisible,
            disableUsersList: disableUsersList
        });
    }

    handleUserSelectionChange(selectedChangedTo: { id: number, permissions: UserAreaPermissions }[]) {
        const { selectedUsers, selectedArea } = this.state;

        const updated = selectedUsers
            .filter(su => selectedChangedTo.map(s => s.id).indexOf(su.id) == -1)
            .map(o => ({ id: o.id, permissions: o.permissions } as any));
        selectedChangedTo = selectedChangedTo.concat(updated);

        console.log("selectedChangedTo", selectedChangedTo);
        console.log("selectedAreaUsers", this.state.selectedAreaUsers);

        this.setState({
            selectedUsers: selectedChangedTo,
            userSelectDirty: true
        });
    }

    handleUserSelectionRemove(selectedRemove: { id: number, permissions: UserAreaPermissions }[]) {
        const { selectedUsers } = this.state;

        const updated = selectedUsers.filter(su => selectedRemove.map(s => s.id).indexOf(su.id) == -1).map(o => ({ id: o.id, permissions: o.permissions } as any));

        this.setState({
            selectedUsers: updated,
            userSelectDirty: true
        });
    }

    async saveUserAreas() {
        const { selectedUsers, inheritedUsers } = this.state;
        const userSelected: { id: number, permissions: UserAreaPermissions }[] = selectedUsers.filter(su => inheritedUsers.map(iu => iu.id).indexOf(su.id) == -1)

        console.log(userSelected);

        const added = _.without(userSelected, ...this.state.selectedAreaUsers);
        console.log("added:", added);

        const userAreas = userSelected.map(us => { return { userId: us.id, areaId: this.state.selectedArea.id, permissions: us.permissions } as UserArea });
        await AdminApiController.setUserAreas({ areaId: this.state.selectedArea.id, userAreas } as SetUserAreasArgs);

        // refresh areas
        const updatedUserAreas = await AdminApiController.getUserAreas();
        this.setState({
            selectedAreaUsers: added,
            userSelectDirty: false,
            userAreas: updatedUserAreas.userAreas,
            ownedUserAreas: updatedUserAreas.ownedUserAreas,
        });

        this.updateControlsState();
    }


    handleAreaDelete() {
        const parent = this.state.selectedArea?.parent;
        AdminApiController.deleteArea(this.state.selectedArea.id)
            .then(resp => {
                if (resp.result) {
                    const treeData = this.getTreeData(undefined, resp.areas);
                    this.setState({
                        areas: resp.areas,
                        treeData: treeData,
                    });
                    this.handleAreaSelectionChange(parent);
                } else {
                    this.setState({ errorMessage: resp.message });
                }
            });
    }

    renderInnerTab = () => {
        const deleteDisabled = !this.state.selectedArea || (!!this.state.selectedArea.children && this.state.selectedArea.children.length > 0) || this.state.selectedArea.isProject;
        const { addButtonVisible, editButtonVisible, deleteButtonVisible, disableUsersList } = this.state;

        return (
            <React.Fragment>
                <h2>Areas</h2>
                <div>
                    <div className="area-upper">
                        <AreaTree
                            data={this.state.treeData}
                            onSelectionChange={this.handleAreaSelectionChange}
                        />
                        {hasEditAreasAccess(this.props.userProfile) && (
                            <div className="area-buttons">
                                {!hasEditSites(this.props.userProfile) && ( // always hide new Client ID button for Edit Sites users
                                    <AreaEditModal
                                        area={new Area()}
                                        parent={null}
                                        mode="new"
                                        callback={this.updateState}
                                    />
                                )}
                                {addButtonVisible && (
                                    <AreaEditModal
                                        area={Object.assign(new Area(), this.state.selectedArea)}
                                        parent={this.state.selectedArea}
                                        mode="add"
                                        callback={this.updateState}
                                    />
                                )}
                                {editButtonVisible && (
                                    <AreaEditModal
                                        area={this.state.selectedArea}
                                        parent={this.getParentArea(this.state.selectedArea)}
                                        mode="edit"
                                        callback={this.updateState}
                                    />
                                )}
                                {deleteButtonVisible && (
                                    <Button
                                        variant="secondary"
                                        className="btn btn-delete"
                                        disabled={deleteDisabled}
                                        onClick={() => {
                                            if (
                                                window.confirm(
                                                    "Are you sure you want to delete this item?"
                                                )
                                            )
                                                this.handleAreaDelete();
                                        }}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {hasViewUsersAccess(this.props.userProfile) ? (
                        <UserAreaListBox
                            options={this.state.users.map(user => ({
                                ...user,
                                value: user.userID,
                                label: user.userName
                            }))}
                            disabled={!this.state.selectedArea || disableUsersList}
                            selected={this.state.selectedUsers}
                            onChange={this.handleUserSelectionChange}
                            onRemove={this.handleUserSelectionRemove}
                            inherited={this.state.inheritedUsers}
                            userAreas={this.state.userAreas}
                            users={this.state.users}
                        />
                    ) :
                        <div style={{ marginTop: "10px" }} className="alert alert-danger" role="alert">You don't have permissions to view Users.</div>
                    }
                    {(this.state.userSelectDirty && hasEditAreasAccess(this.props.userProfile)) && (
                        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                            <Button
                                variant="primary"
                                className="btn btn-save"
                                onClick={() => this.saveUserAreas()}
                            >
                                Save
                            </Button>
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    }

    render() {
        return (
            <div className="areas-inner tab-inner">
                {this.state.errorMessage ? (
                    <div className="alert alert-danger" role="alert">{this.state.errorMessage}</div>
                ) : (
                    this.renderInnerTab()
                )
                }
            </div>
        );
    }

    // initialize list of selected users whenever area selection is changed in tree
    // include existing userArea entries matching the selected area and those inherited from ancestors
    getUsersForSelection(selectedArea: AreaNode): { id: number, permissions: UserAreaPermissions }[] {
        return this.state.userAreas
            .filter(ua => ua.areaId === (selectedArea ? selectedArea.id : null))
            .map(ua => ({ id: ua.userId, permissions: ua.permissions } as any));
    }

    getAncestors(selectedArea: AreaNode): AreaNode[] {
        if (!selectedArea.parent) {
            return [];
        }
        return [selectedArea.parent, ...(this.getAncestors(selectedArea.parent))];
    }

    getInheritedForSelection(selectedArea: AreaNode) {
        const ancestors = this.getAncestors(selectedArea);
        const users = [...new Set(ancestors.map(a => this.getUsersForSelection(a)).reduce((a, b) => a.concat(b), []))];
        return users;
    }

    getParentArea(selectedArea: AreaNode) {
        return this.state.areas.find(
            a => a.id === (selectedArea ? selectedArea.parentId : null)
        );
    }

    getTreeData(parent: AreaNode, items: AreaWithProjectName[], id = null) {
        const { ownedUserAreas } = this.state;
        const accessibleAreaIds = ownedUserAreas.map(a => a.areaId);
        const { userProfile } = this.props;

        const normalizedAreas = this.normalize(parent, items, id);

        if (!hasEditSites(userProfile)) {
            return normalizedAreas;
        }

        return getAccessibleNodes(normalizedAreas);

        // iterates through all nested nodes from parent to children
        // and returns only nodes that user has access to
        function getAccessibleNodes(nodes: AreaNode[]): AreaNode[] {
            var result = [];

            for (var n = 0; n < nodes.length; n++) {
                var node = nodes[n];
                let isChildrensAccessible = childrensAccessible(node);

                if (isChildrensAccessible?.id == node.id) {
                    result.push(node);
                    continue;
                }

                if (isChildrensAccessible) {
                    node.children = getAccessibleNodes(node.children);
                    result.push(node);
                }
            }

            return result;
        }

        // checks all childrens of node if any user has access to any children
        function childrensAccessible(node: AreaNode) {
            if (accessibleAreaIds.some(id => id === node.id)) {
                return node;
            } else if (node.children != null) {
                var i;
                var result = null;
                for (i = 0; result == null && i < node.children.length; i++) {
                    result = childrensAccessible(node.children[i]);
                }
                return result;
            }
            return null;
        }
    }

    // display name of tree node is the lowest truthy entry in hierarchy
    getName(item: AreaWithProjectName) { return item.projectName || item.unit || item.location || item.region || item.clientId };
    normalize(parent: AreaNode, items: AreaWithProjectName[], id = null): AreaNode[] {
        return items
            .filter(item => item.parentId === id)
            .map(item => {
                const node = ({
                    ...item,
                    parent: parent,
                    text: this.getName(item),
                    children: []
                } as any) as AreaNode;
                const children: AreaNode[] = this.normalize(node, items, item.id);
                node.children = children;
                return node;
            });
    }
}